/* Sequoia Fitness | Website: https://www.sequoia.fitness | Author: Ali Hosseini | Copyright (c) 2026 sequoia.fitness. All rights reserved. */
/* Designed and developed by Pouria Shabani | https://linkedin.com/in/pouriashabani */

import { useCallback, useEffect, useRef, useState } from 'react'
import { PROGRAMS } from '../data/programs.js'
import { ChevronLeft, ChevronRight, ShadowCardio, ShadowCorrective, ShadowMobility, ShadowFitness, ShadowFlexibility, ShadowWarmCool, ShadowNutrition } from './icons.jsx'

const SHADOWS = {
  cardio: ShadowCardio,
  corrective: ShadowCorrective,
  mobility: ShadowMobility,
  fitness: ShadowFitness,
  flexibility: ShadowFlexibility,
  'warm-cool': ShadowWarmCool,
  nutrition: ShadowNutrition,
}
const BOXED = new Set(['corrective', 'mobility', 'flexibility', 'warm-cool', 'nutrition'])
const CLONES = 3
const IMG_SRCSET =
  'a/arrow00-1024x1024-7e36dc.webp 1024w, a/arrow00-800x801-4f7ed1.webp 800w, a/arrow00-300x300-279a29.webp 300w, a/arrow00-150x150-9f09f1.webp 150w, a/arrow00-768x769-06309e.webp 768w, a/arrow00-75x75-80e0b6.webp 75w, a/arrow00-480x480-ace933.webp 480w, a/arrow00-96x96-daccd7.webp 96w, a/arrow00-a56861.webp 1379w'
const IMG_SIZES = '(max-width:767px) 480px, (max-width:1024px) 100vw, 1024px'

const perView = () => (window.innerWidth >= 767 ? 3 : 1)
const startIndex = (n) => 6 - (n >> 1)

function Slide({ program, clone, active, index, total }) {
  const Shadow = SHADOWS[program.key]
  const { icon } = program
  const item = (
    <div className="lp-s-item get-program-item">
      <div className="lp-s-head">
        <div className="lp-s-headcol">
          <div className="lp-s-title">
            <h2 className="lp-s-h2">{program.title}</h2>
          </div>
        </div>
        <div className="lp-s-imgw">
          <div className="lp-s-imgwc">
            <img className="lp-s-img" loading="lazy" decoding="async" width="1024" height="1024" src="a/arrow00-1024x1024-7e36dc.webp" srcSet={IMG_SRCSET} sizes={IMG_SIZES} alt="" />
          </div>
        </div>
      </div>
      <div className="lp-s-textw">
        <div className="lp-s-textwc">
          <p className="lp-s-p" dir="rtl">{program.text}</p>
        </div>
      </div>
      <div className="lp-s-shw shadow-head">
        <div className="lp-s-shwc">
          <Shadow className="lp-s-svg" />
        </div>
      </div>
      <a className="lp-s-but get-program-but" href={program.href} {...(program.blank ? { target: '_blank', rel: 'noreferrer' } : {})}>
        <div className="lp-s-butt get-program-but-title">
          <h2 className="lp-s-butth2">{program.cta}</h2>
        </div>
        <div className="lp-s-buti get-program-but-icon">
          <div className="lp-s-butic">
            <img className="lp-s-butimg" loading="lazy" decoding="async" width={icon.width} height={icon.height} src={icon.src} srcSet={icon.srcset} sizes={icon.sizes} alt="" />
          </div>
        </div>
      </a>
    </div>
  )
  return (
    <div
      className={`lp-slide-w lp-slide--${program.key}${clone ? ' is-clone' : ''}${active.mid ? ' is-mid' : ''}`}
      role="group"
      aria-roledescription="slide"
      aria-label={`${index + 1} / ${total}`}
      aria-hidden={active.visible ? undefined : true}
      inert={active.visible ? undefined : true}
    >
      <div className="lp-slide-c">{BOXED.has(program.key) ? <div className="lp-slide-in">{item}</div> : item}</div>
    </div>
  )
}

export default function Programs() {
  const total = PROGRAMS.length
  const viewRef = useRef(null)
  const trackRef = useRef(null)
  const [n, setN] = useState(3)
  const [idx, setIdx] = useState(null)
  const [anim, setAnim] = useState(false)
  const [drag, setDrag] = useState(0)
  const [mid, setMid] = useState(false)
  const busy = useRef(false)
  const dragRef = useRef(null)
  const idxRef = useRef(null)
  idxRef.current = idx

  const current = () => (idxRef.current === null ? startIndex(perView()) : idxRef.current)

  const goTo = useCallback((to) => {
    if (busy.current) return
    busy.current = true
    setDrag(0)
    setAnim(true)
    setIdx(to)
  }, [])

  const settle = () => {
    busy.current = false
    const cur = current()
    setAnim(false)
    if (cur >= CLONES + total) setIdx(cur - total)
    else if (cur < CLONES) setIdx(cur + total)
  }

  useEffect(() => {
    const onResize = () => setN(perView())
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    const onScroll = () => {
      const view = viewRef.current
      if (!view) return
      const r = view.getBoundingClientRect()
      setMid(r.top + 60 < window.innerHeight * 0.7 && r.bottom > 0)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const onMove = (e) => {
      const s = dragRef.current
      if (!s || e.pointerId !== s.id) return
      const dx = e.clientX - s.x
      if (!s.moved && Math.abs(dx) < 10) return
      if (!s.moved) {
        s.moved = true
        try {
          viewRef.current.setPointerCapture(e.pointerId)
        } catch (err) {
          return
        }
      }
      setAnim(false)
      setDrag(dx)
    }
    const onUp = (e) => {
      const s = dragRef.current
      if (!s || e.pointerId !== s.id) return
      dragRef.current = null
      if (!s.moved) return
      const dx = e.clientX - s.x
      const slide = trackRef.current && trackRef.current.children[0]
      const w = slide ? slide.offsetWidth : 300
      const touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
      if (Math.abs(dx) > w * (touch ? 0.3 : 0.5)) {
        busy.current = false
        goTo(current() + (dx > 0 ? 1 : -1))
      } else {
        setAnim(true)
        setDrag(0)
      }
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [goTo])

  const cur = idx === null ? startIndex(n) : idx
  const half = n >> 1
  const slides = [...PROGRAMS.slice(-CLONES).reverse(), ...PROGRAMS, ...PROGRAMS.slice(0, CLONES)]
  const bullet = (((cur - CLONES + half) % total) + total) % total
  const key = (e, fn) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      fn()
    }
  }

  return (
    <div className="lp-programs">
      <div className="lp-car">
        <div
          className="lp-car-view"
          ref={viewRef}
          role="region"
          aria-roledescription="carousel"
          aria-label="Carousel"
          dir="rtl"
          onPointerDown={(e) => {
            if (busy.current || e.button > 0) return
            dragRef.current = { x: e.clientX, id: e.pointerId, moved: false }
          }}
          onDragStart={(e) => e.preventDefault()}
        >
          <div
            className="lp-car-track"
            ref={trackRef}
            aria-live="off"
            style={{
              ...(idx === null ? null : { '--i': idx }),
              '--drag': drag + 'px',
              transitionDuration: anim ? '2000ms' : '0ms',
            }}
            onTransitionEnd={(e) => {
              if (e.target === e.currentTarget) settle()
            }}
          >
            {slides.map((p, t) => {
              const real = t >= CLONES && t < CLONES + total
              const visible = t >= cur && t < cur + n
              return (
                <Slide
                  key={(real ? '' : 'c') + t}
                  program={p}
                  clone={!real}
                  index={((t - CLONES) % total + total) % total}
                  total={total}
                  active={{ visible, mid: mid && t === cur + half }}
                />
              )
            })}
          </div>
        </div>
        <div className="lp-car-arrow lp-car-prev" role="button" tabIndex={0} aria-label="قبل" onClick={() => goTo(current() + 1)} onKeyDown={(e) => key(e, () => goTo(current() + 1))}>
          <ChevronLeft />
        </div>
        <div className="lp-car-arrow lp-car-next" role="button" tabIndex={0} aria-label="بعدی" onClick={() => goTo(current() - 1)} onKeyDown={(e) => key(e, () => goTo(current() - 1))}>
          <ChevronRight />
        </div>
        <div className="lp-car-dots">
          {PROGRAMS.map((p, i) => (
            <span
              key={p.key}
              className={'lp-car-dot' + (i === bullet ? ' is-on' : '')}
              role="button"
              aria-label={'رفتن به اسلاید ' + (i + 1)}
              aria-current={i === bullet ? 'true' : undefined}
              tabIndex={i === bullet ? 0 : undefined}
              onClick={() => goTo(CLONES + i - half)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
